'use client';

import { useState, useEffect } from 'react';
import { cn } from '@/lib/utils';

interface NavItem {
  id: string;
  title: string;
}

interface ArticleNavProps {
  items: NavItem[];
}

export function ArticleNav({ items }: ArticleNavProps) {
  const [activeId, setActiveId] = useState<string>('');

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveId(entry.target.id);
          }
        });
      },
      { rootMargin: '-100px 0px -70% 0px' }
    );

    items.forEach((item) => {
      const element = document.getElementById(item.id);
      if (element) observer.observe(element);
    });

    return () => observer.disconnect();
  }, [items]);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      const yOffset = -100;
      const y = element.getBoundingClientRect().top + window.pageYOffset + yOffset;
      window.scrollTo({ top: y, behavior: 'smooth' });
    }
  };

  const activeIndex = items.findIndex((item) => item.id === activeId);

  return (
    <nav className="sticky top-28">
      <p className="mb-4 text-xs font-semibold uppercase tracking-wider text-neutral-400">
        On this page
      </p>
      <div className="relative">
        {/* Tree line */}
        <div className="absolute left-[5px] top-2 bottom-2 w-px bg-neutral-200" />
        
        {/* Active segment highlight */}
        {activeIndex >= 0 && (
          <div 
            className="absolute left-[5px] w-px bg-neutral-900 transition-all duration-300"
            style={{
              top: `${activeIndex * 36 + 8}px`,
              height: '20px',
            }}
          />
        )}

        <ul className="relative space-y-1">
          {items.map((item, index) => {
            const isActive = item.id === activeId;
            const isPast = activeIndex > index;
            
            return (
              <li key={item.id} className="relative">
                {/* Tree node dot */}
                <div
                  className={cn(
                    "absolute left-0 top-1/2 -translate-y-1/2 w-[11px] h-[11px] rounded-full border-2 transition-colors",
                    isActive
                      ? "bg-neutral-900 border-neutral-900"
                      : isPast
                      ? "bg-neutral-400 border-neutral-400"
                      : "bg-white border-neutral-300"
                  )}
                />
                
                <button
                  onClick={() => scrollToSection(item.id)}
                  className={cn(
                    "block w-full text-left text-sm py-2 pl-6 pr-2 rounded-r-md transition-colors",
                    isActive
                      ? "text-neutral-900 font-medium"
                      : "text-neutral-500 hover:text-neutral-700"
                  )}
                >
                  {item.title}
                </button>
              </li>
            );
          })}
        </ul>
      </div>
    </nav>
  );
}
